// Role color system for UNA member roles
// Provides consistent color coding across all task views

export type MemberRole = 
  | 'managing_member'
  | 'financial_specialist'
  | 'legal_specialist'
  | 'tax_specialist'
  | 'operations_manager'
  | 'board_member_active'
  | 'board_member_passive'
  | 'advisor'
  | 'observer'
  | 'treasury_secretary'
  | 'member';

export interface RoleColors {
  border: string;
  bg: string;
  text: string;
  name: string;
}

const ROLE_COLOR_MAP: Record<MemberRole, RoleColors> = {
  treasury_secretary: {
    border: 'hsl(42, 50%, 54%)', // Antique Gold
    bg: 'hsl(42, 50%, 95%)',
    text: 'hsl(42, 50%, 30%)',
    name: 'Secretary Treasurer',
  },
  managing_member: {
    border: 'hsl(270, 70%, 50%)', // Purple
    bg: 'hsl(270, 70%, 95%)',
    text: 'hsl(270, 70%, 30%)',
    name: 'Managing Member',
  },
  financial_specialist: {
    border: 'hsl(142, 71%, 45%)', // Green
    bg: 'hsl(142, 71%, 95%)',
    text: 'hsl(142, 71%, 25%)',
    name: 'Financial Specialist',
  },
  legal_specialist: {
    border: 'hsl(0, 84%, 60%)', // Red
    bg: 'hsl(0, 84%, 95%)',
    text: 'hsl(0, 84%, 35%)',
    name: 'Legal Specialist',
  },
  tax_specialist: {
    border: 'hsl(25, 95%, 53%)', // Orange
    bg: 'hsl(25, 95%, 95%)',
    text: 'hsl(25, 95%, 30%)',
    name: 'Tax Specialist',
  },
  operations_manager: {
    border: 'hsl(189, 94%, 43%)', // Cyan
    bg: 'hsl(189, 94%, 95%)',
    text: 'hsl(189, 94%, 25%)',
    name: 'Operations Manager',
  },
  board_member_active: {
    border: 'hsl(217, 91%, 60%)', // Blue
    bg: 'hsl(217, 91%, 95%)',
    text: 'hsl(217, 91%, 30%)',
    name: 'Board Member (Active)',
  },
  board_member_passive: {
    border: 'hsl(215, 16%, 47%)', // Gray
    bg: 'hsl(215, 16%, 95%)',
    text: 'hsl(215, 16%, 30%)',
    name: 'Board Member (Passive)',
  },
  advisor: {
    border: 'hsl(262, 83%, 58%)', // Indigo
    bg: 'hsl(262, 83%, 95%)',
    text: 'hsl(262, 83%, 30%)',
    name: 'Advisor',
  },
  observer: {
    border: 'hsl(215, 20%, 65%)', // Slate
    bg: 'hsl(215, 20%, 95%)',
    text: 'hsl(215, 20%, 30%)',
    name: 'Observer',
  },
  member: {
    border: 'hsl(217, 91%, 60%)', // Blue
    bg: 'hsl(217, 91%, 95%)',
    text: 'hsl(217, 91%, 30%)',
    name: 'Member',
  },
};

export function getRoleColor(role: string | null | undefined): RoleColors {
  if (!role) {
    return {
      border: 'hsl(0, 0%, 60%)',
      bg: 'hsl(0, 0%, 95%)',
      text: 'hsl(0, 0%, 40%)',
      name: 'Unknown Role',
    };
  }

  const normalizedRole = role.toLowerCase().replace(/\s+/g, '_') as MemberRole;
  return ROLE_COLOR_MAP[normalizedRole] || ROLE_COLOR_MAP.observer;
}

// Get display name for a role, with optional override for treasury_secretary
export function getRoleDisplayName(role: string | null | undefined, primaryOfficerRole?: string): string {
  if (!role) return 'Unknown Role';
  
  const normalizedRole = role.toLowerCase().replace(/\s+/g, '_') as MemberRole;
  
  // If this is treasury_secretary and we have a custom primary officer role, use it
  if (normalizedRole === 'treasury_secretary' && primaryOfficerRole) {
    return primaryOfficerRole;
  }
  
  const colors = ROLE_COLOR_MAP[normalizedRole];
  return colors?.name || role.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

export function getInitials(name: string): string {
  return name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}
